// src/utils/canvasStorage.js
// ---------------------------------------------------------------------------
// localStorage helpers for the BookCover designer autosave system.
// Keeps a local snapshot of the Fabric.js canvas JSON so that:
//   1. Crash / tab-close loses at most ~400 ms of work.
//   2. Server sync can run less often (every 60 s) without data-loss risk.
// ---------------------------------------------------------------------------

const PREFIX = 'rv_cover_design';

/** Build a deterministic storage key for a draft + version + canvasType. */
function storageKey(draftId, versionId, canvasType = 'front') {
  const suffix = canvasType === 'spine' ? '_spine'
    : canvasType === 'back' ? '_back'
    : canvasType === 'wraparound' ? '_wrap'
    : '';
  return `${PREFIX}_${draftId}_${versionId}${suffix}`;
}

/**
 * Persist the current canvas design JSON to localStorage.
 * @param {string} draftId
 * @param {string} versionId
 * @param {object} designJson  – the object returned by `canvas.toJSON()`
 */
export function setLocalDesign(draftId, versionId, designJson, canvasType = 'front') {
  try {
    const payload = {
      design: designJson,
      savedAt: Date.now(),
    };
    localStorage.setItem(storageKey(draftId, versionId, canvasType), JSON.stringify(payload));
  } catch (err) {
    // localStorage may be full or blocked — non-critical, log and move on
    console.warn('[canvasStorage] Failed to write local design:', err.message);
  }
}

/**
 * Read the most-recent local snapshot for a draft + version.
 * @returns {{ design: object, savedAt: number } | null}
 */
export function getLocalDesign(draftId, versionId, canvasType = 'front') {
  try {
    const raw = localStorage.getItem(storageKey(draftId, versionId, canvasType));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Remove the local snapshot (e.g. after a successful server sync).
 */
export function clearLocalDesign(draftId, versionId, canvasType = 'front') {
  try {
    localStorage.removeItem(storageKey(draftId, versionId, canvasType));
  } catch {
    // non-critical
  }
}
